import { getRequest } from 'Services/baseRequests/getRequest'
import { postRequest } from 'Services/baseRequests/postRequest'
import { RequestResponse } from 'Services/apiResponseTypes'
import {
  GetEmployerDetailsApiResponse,
  GetEmployerDetailsResponse,
  GetEmployersApiResponse,
  GetEmployersResponse,
  GetActiveEmployersApiResponse,
  GetActiveEmployersResponse
} from 'Services/JOONAdmin/adminApiResponseTypes'
import { parseServerDate, formatDateForServer } from 'Utils/helpers/dateUtils'
import { ChargeFeeType, EmployerCountry } from 'Utils/types/employerInfo'
import { CountryCode } from 'Utils/types/countries'
import { Employer } from 'Utils/types/JOONAdmin/employer'
import { putRequest } from 'Services/baseRequests/putRequest'
import { deleteRequest } from 'Services/baseRequests/deleteRequest'

export type GetEmployersPayload = {
  perPage: number
  pageNumber: number
  searchText?: string
}
export const getEmployers = async ({
  searchText,
  perPage,
  pageNumber
}: GetEmployersPayload): Promise<GetEmployersResponse> => {
  const response = await getRequest<GetEmployersApiResponse>(
    `/employers?search_text=${searchText ?? ''}&per_page=${perPage}&page=${pageNumber}`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
      ? {
          employers: response.data.employers.map((e) => ({
            ...e,
            createdAt: parseServerDate(e.createdAt)
          })),
          totalEmployers: response.total_records ?? 0
        }
      : undefined
  }
}

export const getEmployerDetails = async (
  employerId: number
): Promise<GetEmployerDetailsResponse> => {
  const response = await getRequest<GetEmployerDetailsApiResponse>(`/employers/${employerId}`)

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
      ? {
          employer: {
            ...response.data.employer,
            createdAt: parseServerDate(response.data.employer.createdAt)
          }
        }
      : undefined
  }
}

type CreateEmployerPayload = {
  name: string
  contactEmail: string
  employerCountries: Omit<EmployerCountry, 'id' | 'employerId' | 'autoPaymentMethodId'>[]
}

type CreateEmployerApiPayload = {
  name: string
  contact_email: string
  employer_countries_config: {
    country_code: CountryCode
    processing_fee: string
    charge_fee_per: ChargeFeeType
    pricing_description: string
    alternative_reimbursement: boolean
  }[]
}

export const createEmployer = async ({
  name,
  contactEmail,
  employerCountries
}: CreateEmployerPayload) => {
  const payload = {
    name,
    contact_email: contactEmail,
    employer_countries_config: employerCountries.map((ec) => ({
      country_code: ec.countryCode,
      processing_fee: ec.processingFee.toString(),
      charge_fee_per: ec.chargeFeePer,
      pricing_description: ec.pricingDescription || '',
      alternative_reimbursement: ec.alternativeReimbursement
    }))
  }

  const response = await postRequest<RequestResponse, CreateEmployerApiPayload>(
    '/admin/employers',
    payload
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

type CreateEmployerCountryPayload = Omit<
  EmployerCountry,
  'id' | 'employerId' | 'autoPaymentMethodId'
> & {
  employerId: Employer['id']
}

type CreateEmployerCountryApiPayload = {
  country_code: CountryCode
  processing_fee: string
  charge_fee_per: ChargeFeeType
  pricing_description: string
  alternative_reimbursement: boolean
}

export const createEmployerCountry = async ({
  employerId,
  countryCode,
  processingFee,
  chargeFeePer,
  pricingDescription,
  alternativeReimbursement
}: CreateEmployerCountryPayload) => {
  const payload = {
    country_code: countryCode,
    processing_fee: processingFee.toString(),
    charge_fee_per: chargeFeePer,
    pricing_description: pricingDescription || '',
    alternative_reimbursement: alternativeReimbursement
  }

  const response = await postRequest<RequestResponse, CreateEmployerCountryApiPayload>(
    `/admin/employers/${employerId}/create_country`,
    payload
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

type DeleteEmployerCountryPayload = {
  employerId: Employer['id']
  countryCode: CountryCode
}

type DeleteEmployerCountryApiPayload = {
  country_code: CountryCode
}

export const deleteEmployerCountry = async ({
  employerId,
  countryCode
}: DeleteEmployerCountryPayload) => {
  const response = await deleteRequest<RequestResponse, DeleteEmployerCountryApiPayload>(
    `/admin/employers/${employerId}/delete_country`,
    { country_code: countryCode }
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

type FeeScheduleApiPayload = {
  country_code?: CountryCode
  starts_on?: string
  processing_fee: string
  base_processing_fee: string
  charge_fee_per: ChargeFeeType
  alternative_reimbursement?: boolean
}

type CreateFeeSchedulePayload = {
  employerId: Employer['id']
  countryCode: CountryCode
  startsOn: string
  processingFee: number
  baseProcessingFee?: number | null
  chargeFeePer: ChargeFeeType
  alternativeReimbursement: boolean
}

export const createFeeSchedule = async ({
  employerId,
  countryCode,
  startsOn,
  processingFee,
  baseProcessingFee,
  chargeFeePer,
  alternativeReimbursement
}: CreateFeeSchedulePayload) => {
  const payload: FeeScheduleApiPayload = {
    country_code: countryCode,
    starts_on: startsOn,
    processing_fee: processingFee.toString(),
    base_processing_fee: baseProcessingFee != null ? baseProcessingFee.toString() : '',
    charge_fee_per: chargeFeePer,
    alternative_reimbursement: alternativeReimbursement
  }

  const response = await postRequest<RequestResponse, FeeScheduleApiPayload>(
    `/admin/employers/${employerId}/fee_schedules`,
    payload
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

type UpdateFeeSchedulePayload = {
  employerId: Employer['id']
  feeScheduleId: number
  processingFee: number
  baseProcessingFee?: number | null
  chargeFeePer: ChargeFeeType
}

export const updateFeeSchedule = async ({
  employerId,
  feeScheduleId,
  processingFee,
  baseProcessingFee,
  chargeFeePer
}: UpdateFeeSchedulePayload) => {
  const payload: FeeScheduleApiPayload = {
    processing_fee: processingFee.toString(),
    base_processing_fee: baseProcessingFee != null ? baseProcessingFee.toString() : '',
    charge_fee_per: chargeFeePer
  }

  const response = await putRequest<RequestResponse, FeeScheduleApiPayload>(
    `/admin/employers/${employerId}/fee_schedules/${feeScheduleId}`,
    payload
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

type DeleteFeeSchedulePayload = {
  employerId: Employer['id']
  feeScheduleId: number
}

export const deleteFeeSchedule = async ({
  employerId,
  feeScheduleId
}: DeleteFeeSchedulePayload) => {
  const response = await deleteRequest<RequestResponse>(
    `/admin/employers/${employerId}/fee_schedules/${feeScheduleId}`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

type MetaFieldApiPayload = {
  field_name: string
  required: boolean
  unique: boolean
}

type CreateMetaFieldPayload = {
  employerId: Employer['id']
  fieldName: string
  required: boolean
  unique: boolean
}

export const createMetaField = async ({
  employerId,
  fieldName,
  required,
  unique
}: CreateMetaFieldPayload) => {
  const response = await postRequest<RequestResponse, MetaFieldApiPayload>(
    `/admin/employers/${employerId}/meta_fields`,
    { field_name: fieldName, required, unique }
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

type UpdateMetaFieldPayload = CreateMetaFieldPayload & {
  metaFieldId: number
}

export const updateMetaField = async ({
  employerId,
  metaFieldId,
  fieldName,
  required,
  unique
}: UpdateMetaFieldPayload) => {
  const response = await putRequest<RequestResponse, MetaFieldApiPayload>(
    `/admin/employers/${employerId}/meta_fields/${metaFieldId}`,
    { field_name: fieldName, required, unique }
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

type DeleteMetaFieldPayload = {
  employerId: Employer['id']
  metaFieldId: number
}

export const deleteMetaField = async ({ employerId, metaFieldId }: DeleteMetaFieldPayload) => {
  const response = await deleteRequest<RequestResponse>(
    `/admin/employers/${employerId}/meta_fields/${metaFieldId}`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

type UpdatePricingDescriptionPayload = {
  employerId: Employer['id']
  countryCode: CountryCode
  pricingDescription: string
}

type UpdatePricingDescriptionApiPayload = {
  country_code: string
  pricing_description: string
}

export const updatePricingDescription = async ({
  employerId,
  countryCode,
  pricingDescription
}: UpdatePricingDescriptionPayload) => {
  const response = await putRequest<RequestResponse, UpdatePricingDescriptionApiPayload>(
    `/admin/employers/${employerId}/update_country`,
    {
      country_code: countryCode,
      pricing_description: pricingDescription
    }
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

export type TerminateEmployerPayload = {
  employerId: number
  terminationDate: Date
}

type TerminateEmployerApiPayload = {
  termination_date: string
}

export const terminateEmployer = async (
  payload: TerminateEmployerPayload
): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse, TerminateEmployerApiPayload>(
    `/admin/employers/${payload.employerId}/terminate`,
    {
      termination_date: formatDateForServer(payload.terminationDate)
    }
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
  }
}

export const resendPendingInvites = async (
  employerId: number
): Promise<RequestResponse<{ resentCount: number }>> => {
  const response = await postRequest<RequestResponse<{ resentCount: number }>>(
    `/admin/employers/${employerId}/resend_pending_invites`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
  }
}

export const listActiveEmployers = async (
  showCountries: boolean,
  activeOnly = false
): Promise<GetActiveEmployersResponse> => {
  const activeParam = activeOnly ? '&active_only=true' : ''
  const response = await getRequest<GetActiveEmployersApiResponse>(
    `/admin/employers/list_active_employers?countries=${showCountries}${activeParam}`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
      ? {
          employerList: response.data.employerList.map(({ countries, ...rest }) => ({
            ...rest,
            countries: (countries || []).map(({ code }) => code)
          }))
        }
      : undefined
  }
}

export type GenerateCheckinReportsPayload = {
  employerIds: number[]
  startMonth: string
  endMonth: string
}

export const generateCheckinReports = async (
  payload: GenerateCheckinReportsPayload
): Promise<RequestResponse> => {
  const response = await postRequest<
    RequestResponse,
    { employer_ids: number[]; start_month: string; end_month: string }
  >(`/admin/employer_checkin_reports`, {
    employer_ids: payload.employerIds,
    start_month: payload.startMonth,
    end_month: payload.endMonth
  })

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? '',
    errors: response.errors
  }
}
